// src/App.js
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./config/firebaseConfig";

import Login from "./pages/Login";
import { Toaster } from "react-hot-toast";
import Sidebar from "./components/sidebar/Sidebar";
import AdminHeader from "./components/AdminHeader";
import Navbar from "./components/navbar/Navbar";
import HomeAdmin from "./components/home/Home";
import AdminBlogPost from "./components/blog/AdminBlogPost";
import Flavours from "./components/flavours/Flavours";
import Hospitality from "./components/hospitality/HospitalityAdmin";
import About from "./components/about/About";
import Gallery from "./components/gallery/Gallery";
import AdminTestimonialsManager from "./components/testimonials/AdminTestimonialsManager";
import Contact from "./components/contact/Contact";
import AdminPageManager from "./pages/AdminPageManager";
import AdminFaqHero from "./components/faqs/AdminFaqHero";
import Settings from "./pages/Settings";
import EnquiryList from "./pages/EnquiryList";
import SeoManager from "./components/seo/SeoManager";


function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    // Only permit local bypass during explicit local development on localhost
    if (
      process.env.NODE_ENV === 'development' &&
      (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
    ) {
      setUser({ email: 'admin@thevivak.com', uid: 'dev_admin_uid' });
      setLoading(false);
      return;
    }
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Full Cache burst logic for admin panel:
  // When ?refresh=1 is detected, clears caches and forces a fresh network load with ?_t=<timestamp>
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.has('refresh')) {
        if ('caches' in window) {
          caches.keys().then((keys) =>
            Promise.all(keys.map((k) => caches.delete(k)))
          ).catch(() => {});
        }
        const url = new URL(window.location.href);
        url.searchParams.delete('refresh');
        url.searchParams.set('_t', Date.now());
        window.location.replace(url.toString());
      } else if (params.has('_t')) {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete('_t');
        const remainingQuery = cleanUrl.searchParams.toString();
        const finalUrl =
          cleanUrl.pathname +
          (remainingQuery ? `?${remainingQuery}` : '') +
          cleanUrl.hash;
        window.history.replaceState({}, '', finalUrl);
      }
    } catch (e) {
      // Graceful fallback
    }
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }

  return (
    <div className="App flex">
      <Toaster position="top-right" reverseOrder={false} />
      <Sidebar collapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(v => !v)} />
      <main className={`${sidebarCollapsed ? 'ml-16' : 'ml-64'} flex-1 bg-gray-50 min-h-screen transition-all duration-300`}>
        <AdminHeader />
        <div className="p-8">
          <Routes>
            <Route index element={<HomeAdmin />} />
            <Route path="home" element={<HomeAdmin />} />
            <Route path="navbar" element={<Navbar />} />
            <Route path="blogs" element={<AdminBlogPost />} />
            <Route path="flavours-of-vivaak" element={<Flavours />} />
            <Route path="hospitality" element={<Hospitality />} />
            <Route path="about" element={<About />} />
            <Route path="gallery" element={<Gallery />} />
            <Route path="faqs" element={<AdminFaqHero />} />
            <Route path="testimonials" element={<AdminTestimonialsManager />} />
            <Route path="contact" element={<Contact />} />
            <Route path="enquiries" element={<EnquiryList />} />
            <Route path="pages" element={<AdminPageManager />} />
            <Route path="seo" element={<SeoManager />} />
            <Route path="settings" element={<Settings />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

export default App;
